import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + "/panel";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success("Acceso concedido.");
      navigate("/panel");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3.5 font-sans text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-cyber-cyan focus:ring-1 focus:ring-cyber-cyan";

  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-20">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="glass glow-cyan w-full max-w-md rounded-3xl p-8 md:p-10"
        data-testid="auth-card"
      >
        <Link to="/" className="flex items-center gap-2.5" data-testid="auth-logo">
          <img src="/cyvr-logo.png" alt="CYVR" className="h-9 w-9 object-contain drop-shadow-[0_0_10px_rgba(0,240,255,0.5)]" />
          <span className="font-head text-sm font-bold tracking-tight text-white">
            CYVR<span className="text-cyber-cyan"> LABS</span>
          </span>
        </Link>

        <h1 className="mt-8 font-head text-3xl font-bold tracking-tight text-white">
          {mode === "login" ? "Accede a tu panel" : "Crea tu cuenta"}
        </h1>
        <p className="mt-2 flex items-center gap-2 font-sans text-sm text-white/55">
          <ShieldCheck className="h-4 w-4 text-cyber-cyan" />
          Escáneres reales de seguridad para tus sistemas
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
          {mode === "register" && (
            <input data-testid="auth-name" className={input} placeholder="Nombre" value={form.name} onChange={set("name")} required />
          )}
          <input data-testid="auth-email" type="email" className={input} placeholder="Email" value={form.email} onChange={set("email")} required />
          <input data-testid="auth-password" type="password" className={input} placeholder="Contraseña" value={form.password} onChange={set("password")} required />

          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit"
            className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyber-cyan px-8 py-4 font-mono-tech text-xs font-bold uppercase tracking-widest text-[#04060a] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{mode === "login" ? "Entrar" : "Registrarme"} <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
          </button>
        </form>

        <div className="my-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-white/40">o</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <button
          onClick={loginWithGoogle}
          data-testid="auth-google"
          className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white/[0.03] px-8 py-3.5 font-sans text-sm font-medium text-white transition-colors duration-300 hover:border-cyber-cyan hover:bg-white/[0.06]"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
          Continuar con Google
        </button>

        <button
          onClick={() => setMode(mode === "login" ? "register" : "login")}
          data-testid="auth-toggle"
          className="mt-6 w-full text-center font-mono-tech text-xs uppercase tracking-widest text-white/50 transition-colors hover:text-cyber-cyan"
        >
          {mode === "login" ? "¿No tienes cuenta? Regístrate" : "¿Ya tienes cuenta? Inicia sesión"}
        </button>
      </motion.div>
    </div>
  );
}
